// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset } from '../types';
import { num, delta, esc, CHANNEL_COLOUR, CHANNEL_LABEL, STATE_ORDER, STATE_FULL, stateColour } from '../format';
import { multiLine, spark } from '../charts';

export function renderTimeline(root: HTMLElement, data: Dataset): void {
  const { national } = data;
  const years = national.years;
  const N = years.length;

  const lineSeries = (['branch', 'bankpost', 'atm'] as const).map((ch) => ({
    key: ch, label: CHANNEL_LABEL[ch], colour: CHANNEL_COLOUR[ch], values: national.series[ch],
  }));

  const stateCards = STATE_ORDER
    .filter((s) => national.byState[s])
    .map((s) => {
      const b = national.byState[s].branch;
      const change = b[0] ? (b[N - 1] - b[0]) / b[0] : 0;
      return { s, b, now: b[N - 1], first: b[0], change };
    })
    .sort((a, b) => b.now - a.now);

  root.innerHTML = `
    <div class="view-head">
      <h2>Eight years of retreat</h2>
      <p>Physical banking points nationally, each June from ${years[0]} to ${years[N - 1]}. Bank-owned ATMs (steel) fell fastest as the majors retired their fleets; branches (navy) nearly halved; Bank@Post (teal) held.</p>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>National totals by channel</h3><p>Bank branches, Bank@Post outlets and bank-owned ATMs. The cash-use decline through the pandemic (from 2020) and the 2023–24 Senate inquiry into regional bank closures bracket the steepest falls.</p></div>
      <div class="chart-wrap">${multiLine(years, lineSeries, { w: 860, h: 340, fmt: (v) => num(v) })}</div>
      <div class="legend">${lineSeries.map((s) => `<span class="legend-item"><span class="legend-swatch" style="background:${s.colour}"></span>${s.label}</span>`).join('')}</div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Branches by state</h3><p>Each state's branch count over the same period. Every one is down.</p></div>
      <div class="small-mult">
        ${stateCards.map((c) => `
          <div class="cell">
            <h4><span>${esc(STATE_FULL[c.s] ?? c.s)}</span><span class="down">${delta(c.change)}</span></h4>
            <div class="big">${num(c.now)}</div>
            <div style="color:var(--text-tertiary);font-size:var(--font-size-xs);margin-bottom:4px">from ${num(c.first)} in ${years[0]}</div>
            ${spark(c.b, { w: 200, h: 34, colour: stateColour(c.s), fill: true })}
          </div>`).join('')}
      </div>
    </div>
  `;
}
